import { progressApi } from '@/api/progress';
import { uploadsApi } from '@/api/uploads';

import { RECORD_FILE_LOST, apiErrorCode, recordDropCode } from './apiErrorCode';
import { LESSON_AUDIO_KIND, loadLessonAudio, recordAgeMs, recordExt, type LessonAudioPayload } from './lessonAudio';
import { isOnline } from './network';
import { addRecordNotice, recordClientIdOn } from './offlineRecords';
import { onOutboxSettled } from './outbox';
import { addRejectedRecord } from './recordQueue';
import { deleteRecordFile, isRecordFileRef } from './recordStore';
import { publishRecordingDone } from './recordingResult';
import { publishSendProgress } from './sendProgress';
import { UPLOAD_IDLE_MARK } from './uploadWatch';

/**
 * Отправка голосового ответа из очереди — два шага подряд: файл в хранилище, потом отметка ответа настоящей
 * ссылкой. Единственное действие очереди, где лежит не текст, а сама запись: ссылки на неё до загрузки
 * просто не существует.
 *
 * До задачи 120 запись уходила отдельным запросом мимо очереди: оборвалась связь — и работа ученика
 * пропадала совсем. Вынесено из `main.tsx` 02.10.2026, чтобы отправку можно было проверять без запуска
 * приложения; `main.tsx` только регистрирует обработчик.
 *
 * ⚠️ ИМЯ ЗАПИСИ (`client_id`) — ТОЛЬКО ПРИ ВКЛЮЧЁННОМ ФЛАГЕ, И ФЛАГ ЧИТАЕТСЯ ЗДЕСЬ, В МОМЕНТ ОТПРАВКИ (договор
 * `audio-upload.md`, `lib/offlineRecords.ts`). Имя лежит в записи очереди всегда; выключили флаг с сервера —
 * уже лежащая запись уйдёт без него, прежним путём. С именем сервер узнаёт повтор (тот же файл, та же
 * попытка) и отказывает опоздавшей записи вместо того, чтобы вернуть принятую работу на проверку.
 */

/** Имя записи, если его можно слать прямо сейчас. */
function activeClientId(payload: LessonAudioPayload): string | undefined {
  return recordClientIdOn() && payload.clientId ? payload.clientId : undefined;
}

/** Причина неудачи загрузки машинным словом — для журнала доставки (`[a-z0-9_]`, до 32 знаков). */
export function uploadFailDetail(err: unknown): string {
  const e = err as { status?: number; code?: string; message?: string } | undefined;
  if (e?.code === UPLOAD_IDLE_MARK) return 'idle';
  if (!isOnline()) return 'offline';
  const status = typeof e?.status === 'number' ? e.status : 0;
  if (status >= 500) return 'http_5xx';
  if (status >= 400) return `http_${status}`;
  if (/timeout/i.test(`${e?.code ?? ''} ${e?.message ?? ''}`) || e?.code === 'ECONNABORTED') return 'timeout';
  return 'network';
}

/**
 * Известие «запись легла в очередь» — сразу после «Отправить». Сервер запоминает момент записи по своим
 * часам: правило опоздавшей записи тогда работает, даже если сама запись доедет через неделю.
 */
export function announceRecordingQueued(payload: LessonAudioPayload, sizeBytes?: number): void {
  const clientId = activeClientId(payload);
  if (!clientId) return;
  progressApi.recordingAttempt(payload.blockId, {
    client_id: clientId,
    phase: 'queued',
    age_ms: recordAgeMs(payload),
    size_bytes: sizeBytes,
  });
}

export async function sendLessonAudio(payload: LessonAudioPayload): Promise<void> {
  const { blockId, mime, blockType, clientSeconds, stopReason } = payload;
  const type = mime || 'audio/webm';
  // Расширение берём из настоящего типа записи: айфон пишет не webm, а mp4,
  // и раньше такой файл уезжал с чужим именем `recording.webm`.
  // ⚠️ У повтора оно обязано быть тем же: расширение входит в адрес файла на сервере (договор §2).
  const ext = recordExt(type);
  const clientId = activeClientId(payload);
  // ⚠️ Фазы отправки видит блок задания — до этой правки ученик наблюдал одну
  // крутилку в кнопке и не знал, идёт ли что-нибудь вообще. Канал отдельный:
  // загрузка выполняется здесь, вне дерева React (lib/sendProgress.ts).
  try {
    // Байты — из очереди или из файла на телефоне. Файла нет — отказ «навсегда», очередь снимет запись.
    const audio = await loadLessonAudio(payload);
    publishSendProgress(blockId, { phase: 'uploading', percent: 0 });
    let res;
    try {
      res = await uploadsApi.upload(
        new Blob([audio], { type }),
        'homework_audio',
        `recording.${ext}`,
        (percent) => publishSendProgress(blockId, { phase: 'uploading', percent }),
        { clientId },
      );
    } catch (err) {
      // Оборванная загрузка до службы файлов не доходит, и сервер о ней не знал ничего: в сводках считались
      // попытки, а не потери. Известие маленькое — проходит там, где мегабайты не прошли.
      if (clientId) {
        progressApi.recordingAttempt(blockId, {
          client_id: clientId,
          phase: 'upload_failed',
          age_ms: recordAgeMs(payload),
          size_bytes: audio.byteLength,
          detail: uploadFailDetail(err),
        });
      }
      throw err;
    }
    publishSendProgress(blockId, { phase: 'marking', percent: null });
    const answer = await progressApi.answerBlock(blockId, {
      answer_data: res.url,
      is_correct: true,
      block_type: blockType,
      client_seconds: clientSeconds,
      stop_reason: stopReason,
      // Возраст — на момент ЭТОГО запроса: у каждого повтора свой, растущий (договор §3).
      ...(clientId ? { client_id: clientId, age_ms: recordAgeMs(payload) } : {}),
    });
    // ⚠️ Сервер померил файл ПО ЗВУКУ и прислал длину. Показывать в
    // проигрывателе надо её, а не часы страницы: ученица 11.09.2026 видела 2:30
    // у записи на 20 секунд и отправила её, ничего не заподозрив.
    publishRecordingDone(blockId, answer?.recording);
  } finally {
    // ⚠️ Снимать полосу обязательно и при отказе: иначе «Отправляю 40 %»
    // останется висеть поверх сообщения об ошибке, и ученик будет ждать
    // отправку, которой уже нет. Очередь повторит попытку сама.
    publishSendProgress(blockId, null);
  }
}

/**
 * Что остаётся за записью голоса, когда она уходит из очереди.
 *
 * - Файл на телефоне стирается: доставлена, отказана, стёрта учеником — держать его больше незачем.
 * - Отказ «работу уже приняли» / «файла больше нет» мог прийти в фоновом проходе, когда задания на экране
 *   нет: оставляем заметку, задание покажет её при открытии (`takeRecordNotice`).
 * - Запись стёрта с устройства не доставленной (кнопка ученика, перезапись, пропавший файл) — известие
 *   `dropped`: в журнале доставки она иначе числилась бы «не дошла» вечно.
 */
export function initLessonAudio(): () => void {
  return onOutboxSettled((item, how, err) => {
    if (item.kind !== LESSON_AUDIO_KIND) return;
    const payload = item.payload as LessonAudioPayload;
    if (isRecordFileRef(payload.file)) void deleteRecordFile(payload.file);
    const code = how === 'rejected' ? apiErrorCode(err) : undefined;
    if (code && recordDropCode(code)) addRecordNotice(payload.blockId, code);
    // «Не приняты» в профиле — до «Понятно» (промпт 22): заметка у задания показывается один раз и урока не знает.
    // Сюда и пустая или битая запись — её очередь тоже снимает сразу; какие коды берутся — решает `rejectReasonOf`.
    if (code) addRejectedRecord(payload, code);

    const lost = code === RECORD_FILE_LOST;
    if (how === 'removed' || how === 'expired' || lost) {
      const clientId = activeClientId(payload);
      if (!clientId) return;
      progressApi.recordingAttempt(payload.blockId, {
        client_id: clientId,
        phase: 'dropped',
        age_ms: recordAgeMs(payload),
        detail: lost ? 'file_lost' : how === 'expired' ? 'expired' : 'removed',
      });
    }
  });
}
